import React, { useCallback, useMemo, useState } from 'react'
import { ChevronLeft, ChevronRight } from 'lucide-react'

export interface CalendarProps {
    value?: Date | null
    defaultValue?: Date | null
    onChange?: (date: Date) => void
    minDate?: Date
    maxDate?: Date
    style?: React.CSSProperties
}

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']

const isSameDay = (a: Date, b: Date) => a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()

const getCalendarDays = (year: number, month: number) => {
    const firstDay = new Date(year, month, 1).getDay()
    const daysInMonth = new Date(year, month + 1, 0).getDate()
    const daysInPrevMonth = new Date(year, month, 0).getDate()
    const days: { date: Date; current: boolean }[] = []
    for (let i = firstDay - 1; i >= 0; i--) days.push({ date: new Date(year, month - 1, daysInPrevMonth - i), current: false })
    for (let i = 1; i <= daysInMonth; i++) days.push({ date: new Date(year, month, i), current: true })
    const remaining = 42 - days.length
    for (let i = 1; i <= remaining; i++) days.push({ date: new Date(year, month + 1, i), current: false })
    return days
}

const navButtonClass = 'flex size-7 cursor-pointer items-center justify-center rounded-md text-foreground transition-colors duration-150 hover:bg-default'

export const Calendar = ({ value: controlledValue, defaultValue, onChange, minDate, maxDate, style }: CalendarProps) => {
    const today = useMemo(() => new Date(), [])
    const [internalValue, setInternalValue] = useState<Date | null>(defaultValue ?? null)
    const selected = controlledValue !== undefined ? controlledValue : internalValue

    const [calendarView, setCalendarView] = useState<'days' | 'months'>('days')
    const [viewYear, setViewYear] = useState((selected || today).getFullYear())
    const [viewMonth, setViewMonth] = useState((selected || today).getMonth())

    const days = useMemo(() => getCalendarDays(viewYear, viewMonth), [viewYear, viewMonth])
    const monthLabel = new Date(viewYear, viewMonth).toLocaleString('en-US', { month: 'long', year: 'numeric' })

    const goToPrev = useCallback(() => {
        if (viewMonth === 0) {
            setViewMonth(11)
            setViewYear((y) => y - 1)
        } else setViewMonth((m) => m - 1)
    }, [viewMonth])

    const goToNext = useCallback(() => {
        if (viewMonth === 11) {
            setViewMonth(0)
            setViewYear((y) => y + 1)
        } else setViewMonth((m) => m + 1)
    }, [viewMonth])

    const handleSelect = useCallback(
        (date: Date) => {
            if (controlledValue === undefined) setInternalValue(date)
            onChange?.(date)
        },
        [controlledValue, onChange]
    )

    return (
        <div className="flex flex-col" style={style}>
            {/* Month nav */}
            <div className="flex items-center justify-between pb-2">
                {calendarView === 'days' ? (
                    <>
                        <button
                            type="button"
                            onClick={() => setCalendarView('months')}
                            className="flex cursor-pointer items-center gap-1 text-sm font-medium text-foreground"
                        >
                            {monthLabel}
                            <ChevronRight size={16} strokeWidth={1.5} />
                        </button>
                        <div className="flex gap-0.5">
                            <button type="button" onClick={goToPrev} className={navButtonClass}>
                                <ChevronLeft size={16} strokeWidth={1.5} />
                            </button>
                            <button type="button" onClick={goToNext} className={navButtonClass}>
                                <ChevronRight size={16} strokeWidth={1.5} />
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="flex w-full items-center justify-between">
                        <button type="button" onClick={() => setViewYear((y) => y - 1)} className={navButtonClass}>
                            <ChevronLeft size={16} strokeWidth={1.5} />
                        </button>
                        <button type="button" onClick={() => setCalendarView('days')} className="cursor-pointer text-sm font-medium text-foreground">
                            {viewYear}
                        </button>
                        <button type="button" onClick={() => setViewYear((y) => y + 1)} className={navButtonClass}>
                            <ChevronRight size={16} strokeWidth={1.5} />
                        </button>
                    </div>
                )}
            </div>

            {/* Month picker */}
            {calendarView === 'months' && (
                <div className="grid grid-cols-3 gap-1 pb-2">
                    {Array.from({ length: 12 }, (_, i) => {
                        const mLabel = new Date(viewYear, i).toLocaleString('en-US', { month: 'short' })
                        const isActive = i === viewMonth
                        return (
                            <button
                                key={i}
                                type="button"
                                onClick={() => {
                                    setViewMonth(i)
                                    setCalendarView('days')
                                }}
                                className={`flex h-9 cursor-pointer items-center justify-center rounded-[20px] text-sm transition-colors duration-150 ${
                                    isActive ? 'bg-accent font-semibold text-accent-foreground' : 'bg-default font-normal text-foreground'
                                }`}
                            >
                                {mLabel}
                            </button>
                        )
                    })}
                </div>
            )}

            {/* Day grid */}
            {calendarView === 'days' && (
                <>
                    {/* Day headers */}
                    <div className="mb-1 grid grid-cols-7">
                        {DAYS.map((d) => (
                            <div key={d} className="flex items-center justify-center py-2 text-sm font-medium text-foreground-muted">
                                {d}
                            </div>
                        ))}
                    </div>

                    {/* Days */}
                    <div className="grid grid-cols-7">
                        {days.map(({ date, current }, i) => {
                            const isSelected = selected ? isSameDay(date, selected) : false
                            const isToday = isSameDay(date, today)
                            const isDisabled = !!((minDate && date < minDate) || (maxDate && date > maxDate))

                            return (
                                <div key={i} className="relative">
                                    <button
                                        type="button"
                                        disabled={isDisabled}
                                        onClick={() => !isDisabled && handleSelect(date)}
                                        className={`relative flex aspect-square w-full cursor-pointer items-center justify-center rounded-3xl p-0 text-sm transition-colors duration-150 disabled:cursor-default disabled:opacity-50 ${
                                            isSelected
                                                ? 'bg-accent font-medium text-accent-foreground'
                                                : current
                                                  ? 'font-normal text-foreground hover:bg-default'
                                                  : 'font-normal text-foreground-muted'
                                        }`}
                                    >
                                        {date.getDate()}
                                        {isToday && (
                                            <span
                                                className={`pointer-events-none absolute bottom-[3px] left-1/2 size-[3px] -translate-x-1/2 rounded-full ${
                                                    isSelected ? 'bg-accent-foreground' : 'bg-foreground-muted'
                                                }`}
                                            />
                                        )}
                                    </button>
                                </div>
                            )
                        })}
                    </div>
                </>
            )}
        </div>
    )
}
